/**
 * Data types shared across the data layer, stores, and UI.
 *
 * Phase 1 covers projects and classes. Samples and trained model weights
 * arrive in later slices and will live in the same Dexie database.
 */

export type ProjectKind = 'image' | 'audio' | 'pose';

/** Kinds whose samples come from the webcam. Image and pose share the
 *  same capture surface and the same on-disk sample shape (a PNG blob);
 *  pose just runs MoveNet over those PNGs at training time to extract a
 *  joint vector. Use this where the UI cares about "webcam vs mic". */
export function isWebcamKind(kind: ProjectKind): boolean {
  return kind === 'image' || kind === 'pose';
}

export interface Project {
  id: string;
  name: string;
  kind: ProjectKind;
  createdAt: number;
  updatedAt: number;
  /** Phase 2 behavior program — the blocks the kid composed in the
   *  Behavior tab. Optional so old projects open cleanly. The shape
   *  lives in `behavior/types.ts`; it's a pure types module so the
   *  data layer stays free of UI dependencies. */
  blockProgram?: import('../behavior/types').BlockProgram;
  /** Phase 4.5b — when set, this local project has a server-side
   *  mirror with this id. The SyncButton checks this before creating
   *  a new server project so a kid who started from a classroom
   *  assignment doesn't end up with two server-side copies. Set on
   *  initial create-from-assignment and on the first successful
   *  Sync. */
  serverId?: string;
  /** "Make a real app out of it" composer config — what template the
   *  kid picked, theme + text + per-class customisation. Lives on the
   *  project so the same model + the same look survives reload and
   *  travels with .bryta exports. Optional — projects pre-publish or
   *  freshly created don't have it yet. */
  publishConfig?: import('../publish/types').PublishConfig;
  /** Timestamp set when this project was forked out of a gallery
   *  submission. We do NOT store the original submission id — by
   *  product decision, a remix is only labelled, not back-linked.
   *  Used to render a "Remixed" badge on the title and a chip on
   *  the project card. Absent on projects the kid started from
   *  scratch. */
  remixedAt?: number;
}

export interface NewProjectInput {
  name: string;
  kind: ProjectKind;
}

/**
 * Per-class capture overrides. The kid opens the gear icon on a class
 * card to tune these, mirroring TM's per-class settings dialog.
 *
 * - `delaySeconds`: countdown before recording starts (image + audio).
 * - `durationSeconds`: how long to record in one go.
 *   - For audio: how long the recording is before the kid hits Extract
 *     (defaults: 2s for normal classes, 20s for the Background noise
 *     class).
 *   - For image timer-mode: the burst length.
 * - `fps`: frames-per-second for image hold-to-record bursts (default 4,
 *   capped at 30 by the capture loop).
 * - `holdToRecord`: when false, image capture switches to timer mode —
 *   one click triggers `delaySeconds` countdown then a `durationSeconds`
 *   burst at `fps`. When true (default), it's the existing press-and-
 *   hold behavior.
 *
 * All fields optional; the UI falls back to the kind-appropriate
 * defaults when a class has nothing stored.
 */
export interface ClassCaptureSettings {
  delaySeconds?: number;
  durationSeconds?: number;
  fps?: number;
  holdToRecord?: boolean;
}

export interface Class {
  id: string;
  projectId: string;
  name: string;
  position: number;
  createdAt: number;
  updatedAt: number;
  captureSettings?: ClassCaptureSettings;
  /** TM-style "disable class": when true the class is preserved (samples
   *  stay in storage) but excluded from training, eval, and prediction.
   *  Useful for A/B-ing whether a class is helping or hurting. */
  disabled?: boolean;
}

/** A single MoveNet keypoint stored alongside a pose sample.
 *  Coordinates are normalised to [0, 1] of the source frame so the
 *  same keypoints can be drawn at any thumbnail size without storing
 *  device-specific pixel coordinates. */
export interface SamplePoseKeypoint {
  readonly x: number;
  readonly y: number;
  readonly score: number;
}

export interface Sample {
  id: string;
  classId: string;
  /** Denormalized so a project-wide cascade delete can run without joining. */
  projectId: string;
  blob: Blob;
  createdAt: number;
  /** MoveNet keypoints captured at sample time (pose projects only).
   *  Persisted so the kid sees the same overlay on the thumbnail and
   *  in the modal that they saw in the live viewfinder, and so we
   *  don't have to re-run MoveNet at training time. Optional — image
   *  samples and pre-existing pose samples won't have it. */
  poseKeypoints?: ReadonlyArray<SamplePoseKeypoint>;
}

export type SampleSource = 'webcam' | 'upload';

export interface NewSampleInput {
  classId: string;
  blob: Blob;
}

export interface TrainedLabel {
  classId: string;
  /** Snapshot of the class name at training time. The kid may rename a class
   * after training; the saved label preserves what the model was trained on. */
  name: string;
}

/**
 * Metadata for a trained classifier. The actual model weights live in TF.js's
 * own IndexedDB store under `indexeddb://bryta-${projectId}` — we only track
 * what was trained and when, plus the labels in the model's output order.
 */
export interface TrainedModel {
  projectId: string;
  labels: TrainedLabel[];
  /** Number of training samples per class at the time of training. Useful for
   * the eventual dataset-balance surfacing in Phase 1.5. */
  sampleCounts: Record<string, number>;
  trainedAt: number;
  /** Whether the loss curve flattened by the final epoch. Optional — older
   * persisted models (pre-Phase-1-convergence) lack this field; the UI
   * treats `undefined` as "no opinion" and shows the original ready copy. */
  converged?: boolean;
  /** Loss at the last epoch. Persisted so the kid can see how training went
   * after a reload. */
  finalLoss?: number;
  /** Training-set accuracy at the last epoch. */
  finalAccuracy?: number;
  /** All per-epoch losses, oldest first. Used by Phase 1.5's "Under the
   * hood" view; persisted so it survives reload. */
  lossHistory?: number[];
}

/**
 * A picture the kid captured *after* training to test the model with.
 * `classId` is the truth — what the kid says the picture should be —
 * not the model's prediction. The model's prediction is recomputed at
 * scoring time so retraining doesn't leave stale verdicts.
 *
 * For the Phase 1 gate, an eval sample only counts toward held-out
 * accuracy if its `createdAt` is strictly greater than the trained
 * model's `trainedAt`. Samples captured before the most recent training
 * are still kept (the kid might want to inspect them) but flagged stale.
 */
export interface EvalSample {
  id: string;
  classId: string;
  projectId: string;
  blob: Blob;
  createdAt: number;
}
